import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../config/api';
import styles from './BankDetailsEdit.module.scss';
import { useAuth } from '../../context/AuthContext.jsx';

const BankDetailsEdit = ({ item, onSubmit, onClose, loading }) => {
  const { makeAuthenticatedRequest } = useAuth();
  const [formData, setFormData] = useState({
    office_name: '',
    account_head_name: '',
    official_email: '',
    bank_accounts: []
  });

  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize form data when item changes
  useEffect(() => {
    if (item) {
      // Extract office info from the first bank account
      const firstAccount = item.bank_accounts && item.bank_accounts[0];
      
      const formDataToSet = {
        office_name: item.office_name || '',
        account_head_name: item.account_head_name || '',
        official_email: item.official_email || '',
        bank_accounts: item.bank_accounts && item.bank_accounts.length > 0 
          ? item.bank_accounts.map(account => ({
              bank_name: account.bank_name || '',
              account_type: account.account_type || '',
              account_number: account.account_number || ''
            }))
          : [] // Don't add empty rows for editing
      };
      
      setFormData(formDataToSet);
    }
  }, [item]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleBankAccountChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      bank_accounts: prev.bank_accounts.map((account, i) => 
        i === index ? { ...account, [field]: value } : account
      )
    }));
  };



  const removeBankAccount = (index) => {
    if (formData.bank_accounts.length > 1) {
      setFormData(prev => ({
        ...prev,
        bank_accounts: prev.bank_accounts.filter((_, i) => i !== index)
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.office_name.trim()) {
      newErrors.office_name = 'कार्यालयको नाम आवश्यक छ';
    }

    if (!formData.account_head_name.trim()) {
      newErrors.account_head_name = 'लेखा प्रमुखको नाम आवश्यक छ';
    }

    if (!formData.official_email.trim()) {
      newErrors.official_email = 'Official email आवश्यक छ';
    } else if (!/\S+@\S+\.\S+/.test(formData.official_email)) {
      newErrors.official_email = 'कृपया मान्य email address भर्नुहोस्';
    }

    // Check if at least one bank account has data
    const validBankAccounts = formData.bank_accounts.filter(
      account => account.bank_name.trim() || account.account_type.trim() || account.account_number.trim()
    );

    if (validBankAccounts.length === 0) {
      newErrors.bank_accounts = 'कृपया कम्तिमा एउटा बैंक खाताको विवरण भर्नुहोस्।';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitLoading(true);
    setError('');

    try {
      // Filter out empty bank accounts
      const validBankAccounts = formData.bank_accounts.filter(
        account => account.bank_name.trim() || account.account_type.trim() || account.account_number.trim()
      );

      const updateData = {
        office_name: formData.office_name,
        account_head_name: formData.account_head_name,
        official_email: formData.official_email,
        bank_accounts: validBankAccounts,
        last_modified_by: 'User'
      };

      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/bank-details/bulk_update_group/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update bank details');
      }

      // Call the parent onSubmit callback
      if (onSubmit) {
        onSubmit(result);
      }

      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update bank details');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleClose = () => {
    if (!submitLoading) {
      onClose();
    }
  };

  if (!item) {
    return null;
  }



  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>बैंक खाता विवरण सम्पादन गर्नुहोस्</h2>
          <button onClick={handleClose} className={styles.closeButton}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.tableContainer}>
            <table className={styles.formTable}>
              <tbody>
                <tr>
                  <th colSpan="4" className={styles.centerHeader}>
                    मध्यपश्चिम विश्वविद्यालय
                  </th>
                </tr>
                <tr>
                  <td>कार्यालयको नाम.-</td>
                  <td colSpan="3">
                    <input
                      type="text"
                      name="office_name"
                      value={formData.office_name}
                      onChange={(e) => handleInputChange('office_name', e.target.value)}
                      placeholder="कार्यालयको नाम भर्नुहोस्"
                      required
                      className={errors.office_name ? styles.errorInput : ''}
                    />
                    {errors.office_name && (
                      <div className={styles.errorText}>{errors.office_name}</div>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>लेखा प्रमुखको नाम</td>
                  <td colSpan="3">
                    <input
                      type="text"
                      name="account_head_name"
                      value={formData.account_head_name}
                      onChange={(e) => handleInputChange('account_head_name', e.target.value)}
                      placeholder="लेखा प्रमुखको नाम भर्नुहोस्"
                      required
                      className={errors.account_head_name ? styles.errorInput : ''}
                    />
                    {errors.account_head_name && (
                      <div className={styles.errorText}>{errors.account_head_name}</div>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>लेखा प्रमुखको <b>official Mail ID</b></td>
                  <td colSpan="3">
                    <input
                      type="email"
                      name="official_email"
                      value={formData.official_email}
                      onChange={(e) => handleInputChange('official_email', e.target.value)}
                      placeholder="official email भर्नुहोस्"
                      required
                      className={errors.official_email ? styles.errorInput : ''}
                    />
                    {errors.official_email && (
                      <div className={styles.errorText}>{errors.official_email}</div>
                    )}
                  </td>
                </tr>
                <tr>
                  <th colSpan="4" className={styles.centerHeader}>
                    कार्यालयको नाममा सञ्चालित सम्पूर्ण बैक खाताको विवरण
                  </th>
                </tr>
                <tr>
                                          <th>क्र.सं.</th>
                        <th>बैंक नाम</th>
                  <th>खाताको प्रकार</th>
                  <th>खाता नं.</th>
                </tr>
                {formData.bank_accounts.map((account, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <input
                        type="text"
                        value={account.bank_name}
                        onChange={(e) => handleBankAccountChange(index, 'bank_name', e.target.value)}
                        placeholder="बैंकको नाम"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={account.account_type}
                        onChange={(e) => handleBankAccountChange(index, 'account_type', e.target.value)}
                        placeholder="खाताको प्रकार"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={account.account_number}
                        onChange={(e) => handleBankAccountChange(index, 'account_number', e.target.value)}
                        placeholder="खाता नंबर"
                      />
                    </td>
                    {formData.bank_accounts.length > 1 && (
                      <td>
                        <button
                          type="button"
                          onClick={() => removeBankAccount(index)}
                          className={styles.removeButton}
                          title="यो पङ्क्ति हटाउनुहोस्"
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.note}>
            <p><b>नोट:-</b> सम्पूर्ण नमस्कार, माथि उल्लेखित विवरण अनिवार्यरुपमा भर्नुहोस् जानकारी गराइन्छ ।</p>
          </div>

          {error && <div className={styles.error}>{error}</div>}
          {errors.bank_accounts && <div className={styles.error}>{errors.bank_accounts}</div>}

          <div className={styles.formActions}>
            <button
              type="button"
              onClick={handleClose}
              className={styles.cancelButton}
              disabled={submitLoading}
            >
              रद्द गर्नुहोस्
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={submitLoading}
            >
              {submitLoading ? 'अपडेट गर्दै...' : 'अपडेट गर्नुहोस्'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BankDetailsEdit;
